/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Image } from '@fluentui/react/lib/Image';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CopyButton } from 'src/core/components/CopyButton';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logJsonPivotClicked } from 'src/core/services/telemetry/eventsNamesTemplate';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import {
    codeClassName,
    emptyResultStateTextStyles,
    emptyResultWrapperStyles,
    headerClassName,
    resultPivotStyles,
    resultWrapperStyles
} from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';

type AnswerMode = 'json' | 'formatted';

export type ResultsStepProps<T> = {
    results: T;
    isLoading: boolean;
    featureName?: TelemetryFeatureTitle;
    displayTitle?: boolean;
    emptyStateMessage: string;
    children: React.ReactNode;
    emptyStateImageSrc?: string;
    childrenContainerStyles?: IStackProps['styles'];
};

const messages = defineMessages({
    json: { id: 'languageStudio.demoPage.json', defaultMessage: 'JSON' },
    copy: { id: 'languageStudio.demoPage.copy', defaultMessage: 'Copy' },
    result: { id: 'languageStudio.demoPage.result', defaultMessage: 'Result' },
    examineResults: { id: 'languageStudio.demoPage.examineResults', defaultMessage: 'Examine the results' },
    noResultsImageTitle: { id: 'languageStudio.demoPage.noResultsImageTitle', defaultMessage: 'No results yet image' }
});

const getResultContainerProps = (noResultYet: boolean, styles?: IStackProps['styles']): IStackProps => {
    if (noResultYet) {
        return {
            grow: true,
            verticalAlign: 'center',
            horizontalAlign: 'center',
            tokens: { childrenGap: 20 },
            styles: emptyResultWrapperStyles
        };
    }

    return { grow: true, horizontalAlign: 'start', tokens: { childrenGap: 40 }, styles: mergeStyleSets(resultWrapperStyles, styles) };
};

export const ResultsStep = observer(<T extends object>(props: ResultsStepProps<T>) => {
    const {
        children,
        results,
        isLoading,
        featureName,
        emptyStateMessage,
        emptyStateImageSrc,
        displayTitle = true,
        childrenContainerStyles
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { isSignedIn } = rootStore.authStore;
    const [answerMode, setAnswerMode] = React.useState<AnswerMode>('formatted');

    const noResultYet = !results;

    const getResultContent = (): React.ReactNode => {
        if (isLoading) {
            return <Spinner size={SpinnerSize.large} />;
        }

        if (noResultYet) {
            return (
                <>
                    <Image
                        alt={localize(messages.noResultsImageTitle)}
                        src={emptyStateImageSrc ?? getAssetsUrl('assets/images/customText/newEmptyState.svg')}
                    />
                    <LuisText styles={emptyResultStateTextStyles}>
                        {isSignedIn ? emptyStateMessage : localize(commonDemosMessages.signedOutEmptyState)}
                    </LuisText>
                </>
            );
        }

        switch (answerMode) {
            case 'json':
                return (
                    <Stack styles={{ root: { width: '100%' } }}>
                        <Stack horizontal grow horizontalAlign="end">
                            <CopyButton
                                text={localize(messages.copy)}
                                textToCopy={JSON.stringify(results, null, 4)}
                                label={localize(messages.result)}
                            />
                        </Stack>
                        <code>
                            <pre className={codeClassName}>{JSON.stringify(results, null, 4)}</pre>
                        </code>
                    </Stack>
                );
            case 'formatted':
                return children;
            default:
        }
    };
    const handleJsonPivotClicked = (item: PivotItem) => {
        setAnswerMode(item.props.itemKey as AnswerMode);
        logJsonPivotClicked(featureName);
    };

    return (
        <Stack id="demoPageResultsStep" tokens={{ childrenGap: 20 }}>
            {displayTitle && (
                <LuisText as="h2" className={headerClassName} variant="xLarge" fontWeight={600}>
                    {localize(messages.examineResults)}
                </LuisText>
            )}
            <Stack tokens={{ childrenGap: 4 }} styles={{ root: { marginBottom: '5px' } }}>
                <Pivot styles={resultPivotStyles} onLinkClick={item => handleJsonPivotClicked(item)}>
                    <PivotItem itemKey="formatted" headerText={localize(messages.result)} />
                    <PivotItem itemKey="json" headerText={localize(messages.json)} />
                </Pivot>

                <Stack {...getResultContainerProps(noResultYet || isLoading, childrenContainerStyles)}>{getResultContent()}</Stack>
            </Stack>
        </Stack>
    );
});
